<template>
  <div class="p-6 text-slate-900 dark:text-white">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-bold">{{ $t("cars") }} {{ $t("inventory") }}</h2>
      <BaseButton
        type="primary"
        @click="createCar"
        class="bg-primary hover:bg-primary-hover text-white"
        >{{ $t("add_car") }}</BaseButton
      >
    </div>

    <div
      class="bg-white dark:bg-dark-surface shadow rounded p-4 mb-6 border border-slate-200 dark:border-white/5 transition-colors"
    >
      <SearchBar
        v-model="search"
        @search="fetchCars(1)"
        class="dark:bg-dark-input dark:text-white"
        :placeholder="$t('search') + '...'"
      />
    </div>

    <div
      class="bg-white dark:bg-dark-surface shadow rounded overflow-hidden border border-slate-200 dark:border-white/5 transition-colors"
    >
      <div v-if="loading" class="p-12 text-center">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"
        ></div>
        <p class="mt-4 text-slate-500 dark:text-slate-400">
          {{ $t("loading") }}
        </p>
      </div>

      <template v-else>
        <BaseDataTable
          :columns="columns"
          :data="cars"
          class="dark:text-slate-300"
        >
          <!-- Images -->
          <template #images="{ row }">
            <div class="flex gap-1" v-if="row.images && row.images.length">
              <img
                v-for="(img, idx) in row.images.slice(0, 2)"
                :key="idx"
                :src="img.image_path"
                class="w-10 h-10 rounded object-cover border dark:border-white/10"
              />
            </div>
            <span v-else class="text-slate-400 text-xs">{{
              $t("no_data")
            }}</span>
          </template>

          <!-- Brand -->
          <template #brand="{ row }">
            {{ row.brand?.name_en ?? "—" }}
          </template>

          <!-- Owner -->
          <template #user="{ row }">
            {{ row.user?.username ?? "—" }}
          </template>

          <!-- Price -->
          <template #price_per_day="{ row }">
            <span class="font-bold">{{ row.price_per_day }}</span>
            <span class="text-[10px] ml-1 text-slate-400 uppercase">{{
              row.currency
            }}</span>
          </template>

          <!-- Status -->
          <template #status="{ row }">
            <span
              :class="{
                'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-500':
                  row.status === 'available',
                'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-500':
                  row.status === 'rented',
                'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-500':
                  row.status === 'maintenance',
                'bg-gray-100 text-gray-700 dark:bg-slate-500/10 dark:text-slate-500':
                  row.status === 'inactive',
              }"
              class="px-2 py-0.5 rounded-full text-xs font-medium border border-transparent dark:border-current"
            >
              {{ row.status ? $t(row.status) : "—" }}
            </span>
          </template>

          <!-- Category -->
          <template #category="{ row }">
            {{ row.category?.name_en ?? "—" }}
          </template>

          <!-- Actions Slot -->
          <template #actions="{ row }">
            <TableActions
              :row="row"
              show-view
              show-edit
              show-delete
              @view="viewCar($event.id)"
              @edit="editCar($event.id)"
              @delete="deleteCar($event.id)"
            />
          </template>
        </BaseDataTable>

        <div
          v-if="cars.length === 0"
          class="p-12 text-center border-t border-slate-100 dark:border-white/5"
        >
          <p class="text-slate-400">{{ $t("no_data") }}</p>
        </div>

        <div
          class="p-4 border-t border-slate-100 dark:border-white/5"
          v-if="pagination"
        >
          <BasePagination :pagination="pagination" @change-page="fetchCars" />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* No extra custom styles needed, keeping it simple as requested */
</style>

<script>
import BaseButton from "@/components/Button.vue";
import BaseDataTable from "@/components/BaseDataTable.vue";
import BasePagination from "@/components/BasePagination.vue";
import TableActions from "@/components/TableActions.vue";
import SearchBar from "@/components/SearchBar.vue";
import carService from "@/services/car.service";
import { alertSuccess, alertError, alertConfirm } from "@/utils/alert";

export default {
  name: "CarsList",
  components: {
    BaseButton,
    BaseDataTable,
    BasePagination,
    TableActions,
    SearchBar,
  },

  data() {
    return {
      cars: [],
      pagination: null,
      search: "",
      loading: false,
      columns: [
        { label: "ID", field: "id" },
        { label: "Owner", field: "user" },
        { label: "Year", field: "year" },
        { label: "Status", field: "status" },
        { label: "Pricing", field: "price_per_day" },
        { label: "Images", field: "images" },
        { label: "Brand", field: "brand" },
        { label: "Category", field: "category" },
        { label: "Plate Number", field: "plate_number" },
      ],
    };
  },

  watch: {
    "$i18n.locale": {
      immediate: true,
      handler() {
        this.updateColumns();
      },
    },
  },

  methods: {
    updateColumns() {
      this.columns = [
        { label: "ID", field: "id" },
        { label: this.$t("owner"), field: "user" },
        { label: this.$t("year"), field: "year" },
        { label: this.$t("status"), field: "status" },
        { label: this.$t("pricing"), field: "price_per_day" },
        { label: this.$t("images"), field: "images" },
        { label: this.$t("brand"), field: "brand" },
        { label: this.$t("category"), field: "category" },
        { label: this.$t("plate_number"), field: "plate_number" },
      ];
    },
    async fetchCars(page = 1) {
      this.loading = true;
      try {
        const res = await carService.getAll({
          per_page: 10,
          search: this.search,
          page,
        });

        // التعامل مع هيكلية الاستجابة المختلفة
        const responseData = res.data;
        if (responseData.data && Array.isArray(responseData.data)) {
          this.cars = responseData.data;
          this.pagination = responseData.pagination || null;
        } else if (Array.isArray(responseData)) {
          this.cars = responseData;
          this.pagination = null;
        } else {
          this.cars = [];
          this.pagination = null;
        }
      } catch (err) {
        console.error(err);
        alertError("Failed to fetch cars inventory");
      } finally {
        this.loading = false;
      }
    },

    createCar() {
      this.$router.push({ name: "CarCreate" });
    },
    editCar(id) {
      this.$router.push({ name: "CarEdit", params: { id } });
    },
    viewCar(id) {
      this.$router.push({ name: "CarView", params: { id } });
    },

    async deleteCar(id) {
      // if (!confirm("Are you sure you want to delete this car?")) return;
      const confirm = await alertConfirm("Delete this car?");
      try {
        if (confirm.isConfirmed) {
          await carService.delete(id);
          this.cars = this.cars.filter((c) => c.id !== id);
          alertSuccess("Car deleted successfully");
        }
      } catch (err) {
        console.error(err);
      }
    },
  },

  async mounted() {
    await this.fetchCars(1);
  },
};
</script>
